import type { DefaultRuleConfigUpdate } from "@redotech/effect-api-shared/default-rules/config";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { Effect } from "effect";
import { EffectApiClientContext } from "../effect-api-client-provider";

/**
 * Hook that batches default-rule config writes into a single RPC.
 * Settings pages call this after their existing Mongo
 * `updateCurrentTeam` save so support-v2 teams keep the CockroachDB
 * `default_rule_config` rows in sync with legacy
 * `team.settings.support.*`.
 *
 * The input is a discriminated union keyed on `defaultRuleId`, so
 * specifying a literal rule id narrows `configurations` to that rule's
 * config type — mismatched pairs are a TypeScript error at the call
 * site.
 *
 * Example:
 *
 *   const batchUpdateDefaultRuleConfigs = useBatchUpdateDefaultRuleConfigs();
 *   await batchUpdateDefaultRuleConfigs([
 *     ticketRulesToAssignConfig(value),
 *     ticketRulesToUnassignConfig(value),
 *   ]);
 */
export function useBatchUpdateDefaultRuleConfigs(): (
  updates: readonly DefaultRuleConfigUpdate[],
) => Promise<void> {
  const effectApi = useRequiredContext(EffectApiClientContext);

  return useHandler(async (updates: readonly DefaultRuleConfigUpdate[]) => {
    if (updates.length === 0) {
      return;
    }
    await effectApi.runPromise(
      effectApi
        .getClient()
        .defaultRule.batchUpdateDefaultRuleConfigs({ payload: { updates } })
        .pipe(Effect.orDie),
    );
  });
}
